import { useMemo } from "react";

import uniqBy from "lodash/uniqBy";

import { ConfigurableVariableOption } from "@regal-voice/ui";
import { useContactAttributesFullListQuery } from "ServerData/EventSchema/Hooks/useContactAttributesQuery/useContactAttributesQuery";
import { useEventsAttributesFullListQuery } from "ServerData/EventSchema/Hooks/useEventsAttributesQuery/useEventsAttributesQuery";
import { createOptionsWithPrefix, toContactsFieldLookup } from "ServerData/EventSchema/Utils/eventSchemaUtils";

import {
    CURRENT_TIME_LOCAL_TEMPLATE,
    FIELD_TYPE_DATE,
    FIELD_TYPE_OBJECT,
    FIELD_TYPE_LIST,
    FIELD_TYPE_STRING,
} from "../../../../../../../../constants";
import { useAutocompleteQuery } from "./useAutocompleteQuery/useAutocompleteQuery";

import type { ConditionalType } from "@regal-voice/shared-types";
import type { NodeTriggerType } from "Types/Journey";

type EventTrigger = {
    eventType?: NodeTriggerType | ConditionalType;
    eventName?: string;
};

type HookProps = {
    currentSchema: string;
    triggeringEvent: EventTrigger;
    filterType: string;
};

const NOW_VARIABLE_OPTION = new ConfigurableVariableOption(
    CURRENT_TIME_LOCAL_TEMPLATE,
    "now",
    FIELD_TYPE_DATE,
    "now (contact local time)"
);

export function useUpdateProfileLexicalOptions({ currentSchema, triggeringEvent, filterType }: HookProps) {
    // ATTRS
    const { loading: loadingAttrs, contactAttributes } = useContactAttributesFullListQuery({
        formatToObject: false,
    });

    const contactEventOptions = useMemo(() => toContactsFieldLookup(contactAttributes), [contactAttributes]);
    // EVENTS
    const { loading: eventLoading, eventAttributes } = useEventsAttributesFullListQuery(triggeringEvent);
    const eventOptions = useMemo(() => {
        if (eventAttributes.length > 0) {
            return eventAttributes.map((event) => createOptionsWithPrefix(event, "event"));
        }
        return [];
    }, [eventAttributes]);

    // SCHEMA
    const propertyFieldId = useMemo(() => {
        // @ts-expect-error  ts is being too strict, this is a record, which works like an array
        const filedAttr = contactAttributes.filter((attr: Array<string>) => attr[0] === currentSchema)[0];
        if (filedAttr) {
            return filedAttr[1].fieldId;
        }
        return "";
    }, [currentSchema, contactAttributes]);

    const { dropDownOptions } = useAutocompleteQuery({
        fieldId: propertyFieldId,
    });

    const schemaOptions = useMemo(() => {
        if (dropDownOptions) {
            return (
                dropDownOptions.items
                    .map((item) => {
                        if (item) {
                            // @ts-expect-error types are wrong
                            return new ConfigurableVariableOption(item.originalValue, item.originalValue);
                        }
                    })
                    // make sure we remove undefined values
                    .filter((item) => item)
            );
        }
        return [];
    }, [dropDownOptions]);

    // OPTION DISPLAY
    const customOptions = useMemo(() => {
        const options = [...contactEventOptions, ...eventOptions];
        let filteredOptions = [];
        if (filterType === FIELD_TYPE_LIST || filterType == FIELD_TYPE_OBJECT || filterType === FIELD_TYPE_STRING) {
            filteredOptions = options;
        } else {
            filteredOptions = options.filter((option) => option.dataType === filterType);
        }
        const items = filteredOptions.map(
            (option) => new ConfigurableVariableOption(option.label, option.value, option.dataType)
        );
        if (filterType === FIELD_TYPE_DATE) {
            items.unshift(NOW_VARIABLE_OPTION);
        }
        return uniqBy(items, "value");
    }, [filterType, contactEventOptions, eventOptions]);

    return {
        options: customOptions as Array<ConfigurableVariableOption>,
        schemaOptions: schemaOptions as Array<ConfigurableVariableOption>,
        loading: loadingAttrs || eventLoading,
    };
}
